import { supabase } from "./supabase";

/**
 * Wise Webhook Service
 * Manages webhook subscriptions and transfer status tracking
 * Based on: https://docs.wise.com/api-docs/guides/international-receive/subscribe-to-transfer-state-change
 */

const wiseWebhookService = {
  /**
   * Subscribe to transfer state changes for a profile
   * @param {string} affiliateName - Affiliate name for profile lookup
   * @param {string} webhookUrl - URL to receive webhook notifications
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async subscribeToTransferStateChanges(affiliateName, webhookUrl) {
    try {
      console.log(`🔔 Subscribing to transfer state changes for ${affiliateName}`);

      const { data, error } = await supabase.functions.invoke("wise-recipients", {
        body: {
          action: "subscribe-webhook",
          affiliateName: affiliateName,
          webhookUrl: webhookUrl,
          eventType: "transfers#state-change",
        },
      });

      if (error) {
        console.error("❌ Error subscribing to webhooks:", error);
        throw error;
      }

      if (!data.success) {
        console.error("❌ Webhook subscription failed:", data.error);
        return { success: false, error: data.error };
      }

      console.log("✅ Webhook subscription created:", data.subscription.id);
      return { success: true, data: data.subscription };
    } catch (err) {
      console.error("❌ Error in subscribeToTransferStateChanges:", err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Get transfer status from our database
   * @param {string} transferId - Transfer ID
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async getTransferStatus(transferId) {
    try {
      console.log(`🔍 Getting transfer status for: ${transferId}`);

      const { data, error } = await supabase
        .from('wise_transfer_status')
        .select('*')
        .eq('transfer_id', transferId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No record found
          return { success: true, data: null };
        }
        console.error('❌ Error fetching transfer status:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data };
    } catch (err) {
      console.error('❌ Error in getTransferStatus:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Get transfer status history (all webhook events for a transfer)
   * @param {string} transferId - Transfer ID
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async getTransferStatusHistory(transferId) {
    try {
      console.log(`📋 Getting transfer status history for: ${transferId}`);

      const { data, error } = await supabase
        .from('wise_transfer_webhooks')
        .select('*')
        .eq('transfer_id', transferId)
        .order('occurred_at', { ascending: true });

      if (error) {
        console.error('❌ Error fetching transfer history:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data };
    } catch (err) {
      console.error('❌ Error in getTransferStatusHistory:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Get all transfers with their current status
   * @param {string} affiliateName - Optional affiliate name filter
   * @returns {Promise<{success: boolean, data?: any, error?: string}>}
   */
  async getAllTransferStatuses(affiliateName = null) {
    try {
      console.log(`📊 Getting all transfer statuses${affiliateName ? ` for ${affiliateName}` : ''}`);

      let query = supabase
        .from('wise_transfer_status')
        .select('*')
        .order('last_updated', { ascending: false });

      if (affiliateName) {
        query = query.eq('affiliate_name', affiliateName);
      }

      const { data, error } = await query;

      if (error) {
        console.error('❌ Error fetching transfer statuses:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data: data };
    } catch (err) {
      console.error('❌ Error in getAllTransferStatuses:', err);
      return { success: false, error: err.message };
    }
  },

  /**
   * Format transfer status for display
   * @param {Object} transferStatus - Transfer status object
   * @returns {Object} Formatted transfer status
   */
  formatTransferStatus(transferStatus) {
    if (!transferStatus) return null;

    const statusDisplayMap = {
      "incoming_payment_waiting": { label: "Waiting for Payment", color: "yellow", icon: "⏳" },
      "processing": { label: "Processing", color: "blue", icon: "🔄" },
      "funds_converted": { label: "Funds Converted", color: "blue", icon: "💱" },
      "outgoing_payment_sent": { label: "Payment Sent", color: "green", icon: "✅" },
      "delivered": { label: "Delivered", color: "green", icon: "🎉" },
      "bounced_back": { label: "Bounced Back", color: "red", icon: "❌" },
      "funds_refunded": { label: "Refunded", color: "orange", icon: "↩️" },
      "charged_back": { label: "Charged Back", color: "red", icon: "🔙" },
      "cancelled": { label: "Cancelled", color: "gray", icon: "⚫" },
    };

    const statusInfo = statusDisplayMap[transferStatus.current_state] || {
      label: transferStatus.current_state,
      color: "gray",
      icon: "❓"
    };

    return {
      transferId: transferStatus.transfer_id,
      currentState: transferStatus.current_state,
      previousState: transferStatus.previous_state,
      lastUpdated: transferStatus.last_updated,
      affiliateName: transferStatus.affiliate_name,
      amount: transferStatus.amount,
      currency: transferStatus.currency,
      display: {
        label: statusInfo.label,
        color: statusInfo.color,
        icon: statusInfo.icon,
        isCompleted: ["outgoing_payment_sent", "delivered"].includes(transferStatus.current_state),
        isFailed: ["bounced_back", "funds_refunded", "charged_back", "cancelled"].includes(transferStatus.current_state),
        isProcessing: ["processing", "funds_converted"].includes(transferStatus.current_state),
        isWaiting: ["incoming_payment_waiting"].includes(transferStatus.current_state),
      }
    };
  },

  /**
   * Check if a transfer is in a final state (completed or failed)
   * @param {string} currentState - Current transfer state
   * @returns {boolean} True if transfer is in final state
   */
  isTransferFinal(currentState) {
    const finalStates = [
      "outgoing_payment_sent",
      "delivered", 
      "bounced_back",
      "funds_refunded",
      "charged_back",
      "cancelled"
    ];
    return finalStates.includes(currentState);
  },

  /**
   * Get webhook URL for the current environment
   * @returns {string} Webhook URL
   */
  getWebhookUrl() {
    // In production, this should be your actual domain
    const baseUrl = window.location.origin;
    return `${baseUrl}/functions/v1/wise-webhooks`;
  }
};

export { wiseWebhookService };